import {
  IS_DEV,
  IS_NODE,
  IS_NODE_BROWSERLESS,
  IS_TESTING,
} from "@/__main__/constants.mjs";
import blitzMessage, { EVENTS } from "@/__main__/ipc-core.mjs";
import mainRefs from "@/__main__/refs.mjs";
import { svg as BlitzLogo } from "@/inline-assets/blitz-logo.svg";
import { NetworkError } from "@/util/custom-errors.mjs";
import globals from "@/util/global-whitelist.mjs";
import hasQueryString from "@/util/has-query-string.mjs";

let memoizedLabel;

const cssLabel = () => {
  if (memoizedLabel) return memoizedLabel;
  const logo = `data:image/svg+xml;base64,${btoa(BlitzLogo)}`;
  const value = `
    color: #dd364d;
    font-size: 15px;
    background: url(${logo}) left center no-repeat;
    background-size: auto 18px;
`;
  memoizedLabel = value;
  return value;
};

const cssText = (method: string) => `
  color: ${(() => {
    switch (method) {
      case "warn":
        return "#ffcf21";
      case "error":
        return "#ff311f";
      case "debug":
        return "#965d36";
      default:
        return "#4191ab";
    }
  })()};
  ${method === "warn" ? "font-size: 20px;" : ""}
`;

const formatArgs = (args: unknown[]) =>
  args.map((v) => {
    if (
      IS_NODE_BROWSERLESS &&
      v &&
      typeof v === "object" &&
      !(v instanceof Error)
    )
      return "<truncated object>";
    return v;
  });

// Some clever type narrowing
type ConsoleMethods = KeysWithValsOfType<Console, (_: 0) => void>;

class DevError extends Error {
  isInternal: boolean;
  internalClass: string;
}

// Expose dev logging with query param...
const IS_DEV_LOGGING = hasQueryString("__BLITZ_LOG__");

function devConsole(method: ConsoleMethods, ...args) {
  if ((!IS_DEV || IS_TESTING || IS_NODE) && !IS_DEV_LOGGING) return;

  // We want playwright to be able to filter out logs in certain cases
  if (globals.__BLITZ_LOG_FILTER__?.includes(method) === false) {
    return;
  }

  // Playwright outputs log styles in their entirety rather than styling the log message
  if (globals.__BLITZ_LOG_STYLED__ === false) {
    const c = console;
    // @ts-ignore
    c[method](args[0], ...formatArgs(args.slice(1)));
  } else {
    const c = console;
    c[method](
      `%c  %c ${args[0]}`,
      cssLabel(),
      cssText(method),
      // @ts-ignore
      ...formatArgs(args.slice(1)),
    );
  }
}

export const devDebug = devConsole.bind(null, "debug");
export const devLog = devConsole.bind(null, "info");
export const devWarn = (...args) => {
  devConsole("warn", ...args);
  const { router } = mainRefs;
  if (!router || !IS_DEV) return;
  const error =
    (args.find((arg) => arg instanceof Error) as DevError) ||
    new DevError(args[0]);
  error.isInternal = true;
  error.internalClass = "warning";
  router.error = error;
  router.events.emit(router.events.EVENT_LOADED_DATA);
};
export const devError = (...args) => {
  devConsole("error", ...args);
  const { router } = mainRefs;
  if (!router || !IS_DEV) return;
  const error =
    (args.find((arg) => arg instanceof Error) as DevError) ||
    new DevError(args[0]);
  error.isInternal = true;
  error.internalClass = "error";
  router.error = error;
  router.events.emit(router.events.EVENT_LOADED_DATA);
};

const wrapAppLog = (devFn, eventKey) => {
  return (message: string, data?: unknown) => {
    devFn(message, data);
    blitzMessage(EVENTS[eventKey], `${message} ${JSON.stringify(data)}`);
  };
};

/**
 * Only use these if you mean to log to the app log file
 */
export const appDebug = wrapAppLog(devDebug, "LOG_INFO");
export const appLog = wrapAppLog(devLog, "LOG_INFO");
export const appWarn = wrapAppLog(devWarn, "LOG_WARN");
export const appError = wrapAppLog(devError, "LOG_ERROR");

// Make unhandled promise rejections big and ugly.
if (!IS_NODE_BROWSERLESS) {
  globals.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      devWarn("UNHANDLED PROMISE REJECTION!", event.reason);
      if (IS_DEV) event.preventDefault();
    },
  );
}

if (IS_DEV) {
  globalThis.__BLITZ_DEV__ = globals.__BLITZ_DEV__; // eslint-disable-line no-restricted-properties
}

if (!IS_NODE_BROWSERLESS && globals.location?.host === "localhost:3000") {
  const eventSource = new EventSource("/dev-events");
  let didOpen = false;

  eventSource.addEventListener("message", (event) => {
    const changes = JSON.parse(event.data);
    const update = changes.find(({ type }) => type === "update");
    if (update) {
      if (__BLITZ_RELOAD__) {
        devDebug(`Reloading due to update in "${update.path}"!`);
        globals.location.reload();
        return;
      }

      const { router } = mainRefs;
      const error = new DevError(`File changed: ${update.path}`);
      error.isInternal = true;
      router.error = error;
      router.events.emit(router.events.EVENT_LOADED_DATA);
    }
  });

  eventSource.addEventListener("open", () => {
    didOpen = true;
  });

  eventSource.addEventListener("error", () => {
    eventSource.close();
    if (!didOpen) return;
    mainRefs.components.ContentWrapper = () => {
      const error = new NetworkError(
        "The dev server has stopped running. Please refresh the browser when it has restarted.",
      );
      error.name = "Dev server has stopped 😵";
      throw error;
    };
  });
}
